import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal, Empty, Tag, Spin, Alert, Button, Space, Table, Tabs } from 'antd';
import {
  AlertOutlined, MailOutlined, PrinterOutlined, FileWordOutlined,
  CheckCircleTwoTone, ExclamationCircleOutlined, ArrowRightOutlined,
} from '@ant-design/icons';
import bilateralesService from '../../../services/bilateralesService';
import { formatDate } from '../../../variables/constants';

import { useTranslation } from 'react-i18next';
import { avecLibelleTraduit } from '../../../i18n/libelles';

const TYPE_LABEL = avecLibelleTraduit({
  PIECE_MANQUANTE: { color: 'red' },
  RC_NON_DEFINIE: { color: 'orange' },
  PV_ABSENT: { color: 'orange' },
  GARANTIE_NON_CONFIRMEE: { color: 'orange' },
  LITIGE_EN_COURS: { color: 'volcano' },
  DEJA_DISCUTE: { color: 'gold' },
}, 'bilaterales:insuffisances.types');

const G = '#1a6b3c';
const MUTED = '#94a3b8';
const BORDER = '#e2e8f0';

/**
 * Pré-checklist par compagnie - format lettre administrative imprimable.
 * Une compagnie = un onglet = une lettre officielle Word téléchargeable
 * pour dépôt physique.
 */
export default function PreChecklistModal({ open, rbId, rbReference, sessions = [], onClose }) {
  const { t } = useTranslation('bilaterales');
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !rbId) return;
    setLoading(true);
    bilateralesService.preChecklist(rbId)
      .then(setItems)
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [open, rbId]);

  // Group items by compagnie (label)
  const grouped = useMemo(() => {
    const map = {};
    (items || []).forEach((it) => {
      const k = it.sessionCompagnieTrackingId || '-';
      if (!map[k]) {
        map[k] = {
          sessionTrackingId: it.sessionCompagnieTrackingId,
          compagnie: it.compagnieRaisonSociale,
          dossiers: [],
        };
      }
      map[k].dossiers.push(it);
    });
    return Object.values(map);
  }, [items]);

  const totalDossiers = items?.length || 0;
  const totalAvecInsuf = (items || []).filter((i) => i.nbInsuffisances > 0).length;
  const totalBloquants = (items || []).filter((i) => i.bloquant).length;

  const telechargerLettre = (sessionId, compNom) => {
    bilateralesService.downloadExport(
      bilateralesService.lettrePreChecklistUrl(sessionId),
      `Lettre-${rbReference}-${(compNom || 'compagnie').replace(/\s+/g, '_')}.docx`,
    );
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width={1000}
      title={(
        <Space>
          <AlertOutlined style={{ color: '#f97316' }} />
          <span>{t('preChecklist.titre', { reference: rbReference })}</span>
        </Space>
      )}
      footer={(
        <Space>
          <Button onClick={onClose}>{t('actions.fermer')}</Button>
        </Space>
      )}
    >
      {loading ? (
        <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
      ) : !items?.length ? (
        <Empty description={t('preChecklist.aucunDossier')} />
      ) : (
        <>
          <Alert
            type={totalBloquants > 0 ? 'error' : (totalAvecInsuf > 0 ? 'warning' : 'success')}
            showIcon
            message={
              totalBloquants > 0
                ? t('preChecklist.bloquants', { n: totalBloquants })
                : totalAvecInsuf > 0
                  ? t('preChecklist.aRegulariser', { n: totalAvecInsuf, total: totalDossiers })
                  : t('preChecklist.enOrdre')
            }
            style={{ marginBottom: 16 }}
          />

          <p style={{ color: MUTED, marginBottom: 12, fontSize: 13 }}>
            <MailOutlined /> {t('preChecklist.aide')}
          </p>

          <Tabs
            type="card"
            items={grouped.map((g) => {
              const nbInsuf = g.dossiers.filter((d) => d.nbInsuffisances > 0).length;
              const nbBloquant = g.dossiers.filter((d) => d.bloquant).length;
              return {
                key: g.sessionTrackingId,
                label: (
                  <Space>
                    <span style={{ fontWeight: 600 }}>{g.compagnie}</span>
                    <Tag>{g.dossiers.length}</Tag>
                    {nbBloquant > 0
                      ? <Tag color="red">{t('preChecklist.nbBloquant', { n: nbBloquant })}</Tag>
                      : (nbInsuf > 0 && <Tag color="orange">{t('preChecklist.nbARegulariser', { n: nbInsuf })}</Tag>)}
                  </Space>
                ),
                children: (
                  <CompagnieDetail
                    grp={g}
                    rbReference={rbReference}
                    onDownload={() => telechargerLettre(g.sessionTrackingId, g.compagnie)}
                    onOpenSinistre={(id) => navigate(`/sinistres/${id}`)}
                  />
                ),
              };
            })}
          />
        </>
      )}
    </Modal>
  );
}

/* ─── Détail compagnie (tableau imprimable + lettre) ─── */
function CompagnieDetail({ grp, rbReference, onDownload, onOpenSinistre }) {
  const { t } = useTranslation('bilaterales');
  const cols = [
    {
      title: t('preChecklist.colonnes.numeroSinistre'),
      dataIndex: 'numeroSinistreLocal',
      width: 140,
      render: (v, r) => (
        <Button type="link" onClick={() => onOpenSinistre(r.sinistreTrackingId)}
          style={{ paddingLeft: 0, fontWeight: 600 }}>
          {v} <ArrowRightOutlined style={{ fontSize: 11 }} />
        </Button>
      ),
    },
    { title: t('session.colonnes.victime'), dataIndex: 'victimeNomComplet', render: (v) => v || '-', width: 160 },
    {
      title: t('preChecklist.colonnes.etat'),
      width: 110,
      render: (_, r) => (
        r.bloquant
          ? <Tag color="red" icon={<ExclamationCircleOutlined />}>{t('preChecklist.bloquant')}</Tag>
          : (r.nbInsuffisances === 0
            ? <Tag color="green" icon={<CheckCircleTwoTone twoToneColor="#16a34a" />}>OK</Tag>
            : <Tag color="orange">{t('preChecklist.aCompleter')}</Tag>)
      ),
    },
    {
      title: t('preChecklist.colonnes.piecesAttendues'),
      render: (_, r) => (
        r.nbInsuffisances === 0
          ? <span style={{ color: '#16a34a' }}>{t('preChecklist.dossierComplet')}</span>
          : (
            <Space direction="vertical" size={4}>
              {(r.insuffisances || []).map((ins, k) => {
                const meta = TYPE_LABEL[ins.type] || { color: 'default', label: ins.type };
                return (
                  <Space key={k} size={6} wrap>
                    <Tag color={meta.color}>{meta.label}</Tag>
                    <span>{ins.libelle}</span>
                    {ins.details && <span style={{ color: MUTED, fontSize: 12 }}>- {ins.details}</span>}
                  </Space>
                );
              })}
            </Space>
          )
      ),
    },
  ];

  return (
    <div>
      <div style={{
        display: 'flex', justifyContent: 'space-between',
        alignItems: 'center', marginBottom: 12, padding: '10px 14px',
        background: '#f8fafc', borderRadius: 8, border: `1px solid ${BORDER}`,
      }}>
        <div>
          <div style={{ fontSize: 13, color: MUTED }}>{t('preChecklist.destinataire')}</div>
          <div style={{ fontSize: 16, fontWeight: 700 }}>{grp.compagnie}</div>
          <div style={{ fontSize: 12, color: MUTED }}>
            {t('preChecklist.dossiersExamines', { n: grp.dossiers.length, reference: rbReference })}
          </div>
        </div>
        <Space>
          <Button icon={<FileWordOutlined />} type="primary"
            style={{ background: G, borderColor: G }}
            onClick={onDownload}>
            {t('preChecklist.telechargerLettre')}
          </Button>
        </Space>
      </div>

      <Table
        rowKey="dossierBilateralTrackingId"
        dataSource={grp.dossiers}
        columns={cols}
        size="small"
        pagination={false}
        rowClassName={(r) => r.bloquant ? 'row-bloquant' : ''}
      />

      <style>{`
        .row-bloquant td { background: #fef2f2 !important; }
        .row-bloquant td:first-child { box-shadow: inset 4px 0 0 #ef4444; }
      `}</style>
    </div>
  );
}
